import React from "react";
import { Button } from "@heroui/button";
import { Eye, Pencil, Trash2 } from "lucide-react";

import { Product, Category, formatPrice } from "../../types/menu";

interface ProductsTableProps {
  products: Product[];
  categories: Category[];
  loading: boolean;
  onDelete: (productId: number) => Promise<void>;
  onToggleAvailability: (product: Product) => Promise<void>;
  onView: (productId: number) => void;
  onEdit: (productId: number) => void;
}

const ProductsTable: React.FC<ProductsTableProps> = ({
  products,
  // categories,
  loading,
  onDelete,
  onToggleAvailability,
  onView,
  onEdit,
}) => {
  // Get category name by ID
  // const getCategoryName = (categoryId: number | null): string => {
  //   if (!categoryId) return "Uncategorized";

  //   const category = categories.find((c) => c.id === categoryId);

  //   return category ? category.name : "Unknown";
  // };

  // Get categories list for a product
  const getProductCategories = (product: Product): string => {
    if (!product.categories || product.categories.length === 0) {
      return "Uncategorized";
    }

    return product.categories.map((c) => c.name).join(", ");
  };

  // Confirm product deletion
  const confirmDelete = (product: Product) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${product.name}"? This action cannot be undone.`,
      )
    ) {
      onDelete(product.id);
    }
  };

  // Loading state
  if (loading && products.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block w-8 h-8 border-4 border-t-primary-500 border-r-primary-500/30 border-b-primary-500/30 border-l-primary-500/30 rounded-full animate-spin mb-4" />
        <p className="text-gray-500 dark:text-gray-400">Loading products...</p>
      </div>
    );
  }

  // Empty state
  if (!loading && products.length === 0) {
    return (
      <div className="p-8 text-center">
        <svg
          className="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
        <p className="text-xl font-semibold text-gray-600 dark:text-gray-300 mb-2">
          No products found
        </p>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          Create your first product or adjust your search filters.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Product
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Category
            </th>
            <th
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Price
            </th>
            <th
              className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Status
            </th>
            <th
              className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Featured
            </th>
            <th
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {products.map((product) => (
            <tr
              key={product.id}
              className={
                product.unavailable ? "bg-gray-50 dark:bg-gray-900/30" : ""
              }
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  {product.image_url ? (
                    <div className="flex-shrink-0 h-10 w-10">
                      <img
                        alt={product.name}
                        className="h-10 w-10 rounded-md object-cover"
                        src={product.image_url}
                      />
                    </div>
                  ) : (
                    <div className="flex-shrink-0 h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-md flex items-center justify-center">
                      <svg
                        className="h-6 w-6 text-gray-400 dark:text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                        />
                      </svg>
                    </div>
                  )}
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-white w-60 text-ellipsis overflow-hidden">
                      {product.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 w-40 text-ellipsis overflow-hidden">
                      Code: {product.code}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {getProductCategories(product)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                {formatPrice(product.price)}
                {product.product_types && product.product_types.length > 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 block">
                    {product.product_types.length} size
                    {product.product_types.length > 1 ? "s" : ""}
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    product.unavailable
                      ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
                      : "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                  }`}
                >
                  {product.unavailable ? "Unavailable" : "Available"}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                {product.is_featured ? (
                  <svg
                    className="h-5 w-5 mx-auto text-yellow-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ) : (
                  <span className="text-gray-400 dark:text-gray-600">-</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                <Button
                  color="primary"
                  size="sm"
                  variant="light"
                  onPress={() => onView(product.id)}
                >
                  <Eye className="w-4 h-4 mr-2" /> View
                </Button>
                <Button
                  color="primary"
                  size="sm"
                  variant="light"
                  onPress={() => onEdit(product.id)}
                >
                  <Pencil className="w-4 h-4 mr-2" /> Edit
                </Button>
                <Button
                  color={product.unavailable ? "success" : "warning"}
                  size="sm"
                  variant="light"
                  onPress={() => onToggleAvailability(product)}
                >
                  {product.unavailable ? "Enable" : "Disable"}
                </Button>
                <Button
                  color="danger"
                  size="sm"
                  variant="light"
                  onPress={() => confirmDelete(product)}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProductsTable;
